import { css } from '@microsoft/fast-element';
import { focusStrokeOuter, neutralForegroundFocus, neutralForegroundHover, neutralStealthFillActive, neutralStealthFillHover, neutralStealthFillRest, getToken, getBindingToken, neutralForegroundRest, neutralForegroundActive } from '../../design-tokens';
import { heightNumber } from '../size';
import { display } from '../../foundation/display';
import { colorBehavior, variantBehavior } from '../behaviors';

export interface IButtonStyles {
    variant: ButtonVariant;
    color: string;
}

export type ButtonVariant =
    'filled'
    | 'outline'
    | 'stealth'
    | 'link'
    | 'link-light'

export const buttonStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css<IButtonStyles>`
    :host([disabled]) {
        opacity: ${getToken('color-disabled-opacity')};
        cursor: not-allowed;
    }
    ${BaseButtonStyles(
        interactivitySelector,
        nonInteractivitySelector
    )}
`.withBehaviors(
    colorBehavior('neutral', NeutralButtonColors),
    colorBehavior('primary', PrimaryButtonColors),
    colorBehavior('secondary', SecondaryButtonColors),
    variantBehavior(
        'outline',
        OutlineButtonStyles(
            interactivitySelector,
            nonInteractivitySelector
        )
    ),
    variantBehavior(
        'stealth',
        StealthButtonStyles(
            interactivitySelector,
            nonInteractivitySelector
        )
    ),
    variantBehavior(
        'link',
        LinkStyles(
            interactivitySelector,
            nonInteractivitySelector
        )
    ),
    variantBehavior(
        'link-light',
        LinkLightStyles(
            interactivitySelector,
            nonInteractivitySelector
        )
    )
);


export const NeutralButtonColors = css`
    :host {
        --background-rest: ${getToken('ui-button-color-neutral-background-rest')};
        --background-hover: ${getToken('ui-button-color-neutral-background-hover')};
        --background-active: ${getToken('ui-button-color-neutral-background-active')};
        --background-focus: ${getToken('ui-button-color-neutral-background-focus')};
        --background-focus-outline: ${getToken('ui-button-color-neutral-background-focus-outline')};

        --text-rest: ${neutralForegroundRest};
        --text-hover: ${neutralForegroundHover};
        --text-active: ${neutralForegroundActive};
        --text-focus: ${neutralForegroundFocus};

        --stroke-rest: ${getToken('ui-button-color-neutral-stroke-rest')};
        --stroke-hover: ${getToken('ui-button-color-neutral-stroke-hover')};
        --stroke-active: ${getToken('ui-button-color-neutral-stroke-active')};
        --stroke-focus: ${getToken('ui-button-color-neutral-stroke-focus')};
    }

    :host([variant="filled"]) {
        --text-rest: ${getToken('ui-button-color-neutral-text-rest')};
        --text-hover: ${getToken('ui-button-color-neutral-text-hover')};
        --text-active: ${getToken('ui-button-color-neutral-text-active')};
        --text-focus: ${getToken('ui-button-color-neutral-text-focus')};
    }
`;

export const PrimaryButtonColors = css`
    :host {
        --background-rest: ${getToken('ui-button-color-primary-background-rest')};
        --background-hover: ${getToken('ui-button-color-primary-background-hover')};
        --background-active: ${getToken('ui-button-color-primary-background-active')};
        --background-focus: ${getToken('ui-button-color-primary-background-focus')};

        --text-rest: ${neutralForegroundRest};
        --text-hover: ${neutralForegroundHover};
        --text-active: ${neutralForegroundActive};
        --text-focus: ${neutralForegroundFocus};

        --stroke-rest: ${getToken('ui-button-color-primary-stroke-rest')};
        --stroke-hover: ${getToken('ui-button-color-primary-stroke-hover')};
        --stroke-active: ${getToken('ui-button-color-primary-stroke-active')};
        --stroke-focus: ${getToken('ui-button-color-primary-stroke-focus')};
    }

    :host([variant="filled"]) {
        --text-rest: ${getToken('ui-button-color-primary-text-rest')};
        --text-hover: ${getToken('ui-button-color-primary-text-hover')};
        --text-active: ${getToken('ui-button-color-primary-text-active')};
        --text-focus: ${getToken('ui-button-color-primary-text-focus')};
    }

    :host([variant*="link"]) {
        --text-rest: ${getToken('ui-button-color-primary-background-rest')};
        --text-hover: ${getToken('ui-button-color-primary-background-hover')};
        --text-active: ${getToken('ui-button-color-primary-background-active')};
        --text-focus: ${getToken('ui-button-color-primary-background-focus')};
    }
`;

export const SecondaryButtonColors = css`
    :host {
        --background-rest: ${getToken('ui-button-color-secondary-background-rest')};
        --background-hover: ${getToken('ui-button-color-secondary-background-hover')};
        --background-active: ${getToken('ui-button-color-secondary-background-active')};
        --background-focus: ${getToken('ui-button-color-secondary-background-focus')};

        --text-rest: ${neutralForegroundRest};
        --text-hover: ${neutralForegroundHover};
        --text-active: ${neutralForegroundActive};
        --text-focus: ${neutralForegroundFocus};

        --stroke-rest: ${getToken('ui-button-color-secondary-stroke-rest')};
        --stroke-hover: ${getToken('ui-button-color-secondary-stroke-hover')};
        --stroke-active: ${getToken('ui-button-color-secondary-stroke-active')};
        --stroke-focus: ${getToken('ui-button-color-secondary-stroke-focus')};
    }

    :host([variant="filled"]) {
        --text-rest: ${getToken('ui-button-color-secondary-text-rest')};
        --text-hover: ${getToken('ui-button-color-secondary-text-hover')};
        --text-active: ${getToken('ui-button-color-secondary-text-active')};
        --text-focus: ${getToken('ui-button-color-secondary-text-focus')};
    }

    :host([variant*="link"]) {
        --background-rest: ${getToken('ui-button-color-secondary-background-rest')};
        --background-hover: ${getToken('ui-button-color-secondary-background-hover')};
        --background-active: ${getToken('ui-button-color-secondary-background-active')};
        --background-focus: ${getToken('ui-button-color-secondary-background-focus')};
    }
`;

export const BaseButtonStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css`
    ${display('inline-flex')} :host {
        justify-content: center;
        align-items: center;

        border-style: solid;
        border-color: transparent !important;
        /* TODO: Remove important after tailwind is removed: https://github.com/tailwindlabs/tailwindcss/discussions/2261 */
        border-width: calc(${getToken('space-stroke-width')} * 1px) !important;
        border-radius: calc(${getToken('space-button-corner-radius')} * 1px);
        height: calc(${heightNumber} * 1px);
        min-width: calc(${heightNumber} * 0.5px);

        background-color: var(--background-rest);
        color: var(--text-rest);
        fill: currentColor;

        cursor: pointer;
        outline: none;

        -webkit-appearance: none !important;
    }

    :host .control {
        display: inline-flex;

        flex-grow: 1;
        justify-content: inherit;
        align-items: inherit;

        box-sizing: border-box;

        border: none;
        border-radius: inherit;

        padding: 0 calc((10 + (${getToken('space-design-unit')} * 2 * ${getToken('space-density')})) * 1px);
        height: inherit;

        background: transparent;
        color: inherit;
        fill: inherit;

        font-family: inherit;
        font-size: inherit;
        font-weight: ${getToken('ui-button-typography-font-weight')};
        white-space: nowrap;

        outline: none;
        text-decoration: none;
        cursor: inherit;
    }

    .control,
    .end,
    .start {
        font: inherit;
    }

    .control.icon-only {
        padding: 0;
        line-height: 0;
    }

    :host(${interactivitySelector}:hover) {
        background-color: var(--background-hover);
        color: var(--text-hover);
    }

    :host(${interactivitySelector}:active) {
        background-color: var(--background-active);
        color: var(--text-active);
    }

    :host(:focus-visible) {
        background-color: var(--background-focus);
        color: var(--text-focus);
        border-color: ${focusStrokeOuter} ;
        box-shadow: 0 0 0 calc(${getToken('space-focus-stroke-width')} * 1px) var(--text-focus) inset ;
    }

    .control::-moz-focus-inner {
        border: 0;
    }

    .content {
        pointer-events: none;
    }

    .start,
    .end {
        display: flex;
        pointer-events: none;
    }

    .start {
        margin-inline-end: calc(${getToken('space-design-unit')} * 2px);
    }

    .end {
        margin-inline-start: calc(${getToken('space-design-unit')} * 2px);
    }
`;

// TODO: Using stroke for color is counter intuitive.
export const LinkStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css`
        :host {
            height: auto;
            font-family: inherit;
            font-size: inherit;
            line-height: inherit;
            min-width: auto;
            background: transparent;
            /* needs important because of tailwind */
            border: none !important;
            box-shadow: none;
        }

        :host .control {
            display: inline;
            padding: 0;
            line-height: 1;
            text-decoration: underline;
            text-decoration-thickness: 1px;
        }

        :host(${interactivitySelector}),
        :host(${interactivitySelector}:hover),
        :host(${interactivitySelector}:active) {
            background: transparent;
        }

        :host(:focus-visible) {
            box-shadow: none;
        }

        :host .control${interactivitySelector}:active {
            text-decoration: none;
        }
        :host .control:focus-visible {
            text-decoration: underline;
            text-decoration-thickness: 3px;
        }
    `;

export const LinkLightStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css`
        :host {
            height: auto;
            font-family: inherit;
            font-size: inherit;
            line-height: inherit;
            min-width: auto;
            background: transparent;
            /* needs important because of tailwind */
            border: none !important;
            box-shadow: none;
        }

        :host .control {
            display: inline;
            padding: 0;
            line-height: 1;
            text-decoration-thickness: 1px;
        }
        :host(${interactivitySelector}),
        :host(${interactivitySelector}:hover),
        :host(${interactivitySelector}:active) {
            background: transparent;
        }
        :host(:focus-visible) {
            box-shadow: none;
        }
        :host .control${interactivitySelector}:hover,
        :host .control${interactivitySelector}:active {
            text-decoration: underline;
        }
        :host .control:focus-visible {
            text-decoration: underline;
            text-decoration-thickness: 3px;
        }
    `;

export const OutlineButtonStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css`
        :host {
            background: transparent;
            border-width: calc(${getToken('space-stroke-width')} * 1px) !important;
            border-color: var(--stroke-rest) !important;
        }

        :host(${interactivitySelector}:hover) {
            background: ${getToken('color-neutral-hover')};
            border-color: var(--stroke-hover) !important;
        }

        :host(${interactivitySelector}:active) {
            background: ${getToken('color-neutral-active')};
            border-color: var(--stroke-active) !important;
        }
    `;

export const StealthButtonStyles = (interactivitySelector: string, nonInteractivitySelector: string) => css`
        :host {
            background: ${neutralStealthFillRest};
        }
        :host(${interactivitySelector}:hover),
        :host(${interactivitySelector}:focus-visible) {
            background: ${neutralStealthFillHover};
        }
        :host(${interactivitySelector}:active) {
            background: ${neutralStealthFillActive};
        }
    `;
